/**
 * Splats receiving shadows: shader code shared by every gsplat modifier that composes relighting.
 *
 * Each splat looks up the directional shadow maps (up to MAX_SPLAT_SHADOW_LIGHTS) at its centre.
 * The lookup point is pushed toward the light by a bias that grows with the splat's size, because
 * a splat writes one depth for its whole footprint into the shadow map, so its neighbours on the
 * same surface would otherwise shadow it (acne). The map is filtered over a small disk whose
 * radius is the light's softness plus the splat's own footprint, so a large splat on a shadow
 * edge takes a partial value and edges stay smooth instead of showing splat-sized blocks.
 *
 * The splat's size, flatness and normal (its thinnest axis) come from rl_recordSplatShape, which
 * every composed modifySplatRotationScale calls before modifySplatColor runs. The bias grows with
 * the footprint times how far the surface leans from the light, and on flat splats the filter
 * taps follow the surface's depth slope in the map (receiver-plane bias), so a sloped wall or
 * floor does not shadow itself.
 *
 * The maps are only declared outside the shadow / pick / prepass variants: in the shadow pass the
 * map being sampled is the render target being drawn (a feedback loop in WebGL2 and a validation
 * error in WebGPU), and the other passes don't need the colour.
 *
 * Uniforms (set by ShadowSystem, see shadowSystem.ts):
 *   uSplatShadowParams[slot]  x strength (0 = slot unused), y filter radius (uv), z bias (world), w bias per unit splat size
 *   uSplatShadowLight[slot]   xyz unit vector toward the light, w world → shadow-map uv scale
 *   uSplatShadowMatrix[slot]  world → shadow map (uv in xy, depth in z), the light's own shadow matrix
 *   uSplatShadowGlobal        x extra filter taps (0-8), y 1 when relighting is on (shadows only remove light)
 *   uSplatShadowTexel         x / y world size of a shadow-map texel per slot, z slope bias in texels
 *   uSplatShadowMap0 / 1      the lights' shadow maps (depth, compare sampler)
 *
 * Spot and point lights (up to MAX_SPLAT_LOCAL_SHADOW_LIGHTS, splatLocalShadowFactor) live in the
 * engine's clustered shadow atlas: a spot light is one perspective map in a tile, a point light
 * six cube faces in a 3 x 2 block of a tile, holding distance / range. The shadow system binds a
 * copy of the atlas taken after the engine renders it, with each light's tile, matrix and
 * position as rendered (shadowSystem.ts, LocalShadowReceivers), so the lookup never pairs a map
 * with another render's placement. The bias and filter follow the directional lookup, scaled by
 * the distance to the light (a texel covers more world the further it is from the light).
 *
 * Cloud layers (effects/clouds) add soft, moving cloud shadows: splatCloudShadowFactor projects
 * each splat along the sun onto the layer's column-density map (CLOUD_SHADOW_*, cloudShaders.ts),
 * composed right after this code in the relighting snippet.
 */

export const SPLAT_SHADOW_GLSL = /* glsl */ `
uniform vec4 uSplatShadowParams[2];
uniform vec4 uSplatShadowLight[2];
uniform mat4 uSplatShadowMatrix[2];
uniform vec4 uSplatShadowGlobal;
uniform vec4 uSplatShadowTexel;
#if !defined(SHADOW_PASS) && !defined(PICK_PASS) && !defined(PREPASS_PASS)
uniform highp sampler2DShadow uSplatShadowMap0;
uniform highp sampler2DShadow uSplatShadowMap1;
const vec2 RL_SHADOW_DISK[8] = vec2[8](
    vec2(-0.613, 0.617), vec2(0.170, -0.040), vec2(-0.299, -0.792), vec2(0.645, 0.493),
    vec2(-0.651, -0.118), vec2(0.421, -0.655), vec2(0.003, 0.917), vec2(0.930, -0.110)
);
// texture(), not textureLod(): this runs in the vertex shader, where texture() already reads
// level 0, and Chrome / Firefox on Windows (ANGLE on Direct3D 11) cannot compile textureLod() on a
// shadow sampler there. The draw then fails and the whole scan disappears.
float rl_shadowSample(int slot, vec3 coord) {
    if (slot == 0) return texture(uSplatShadowMap0, coord);
    return texture(uSplatShadowMap1, coord);
}
float splatShadowFactor(int slot, vec3 worldPos, vec3 normal) {
    vec4 params = uSplatShadowParams[slot];
    if (params.x <= 0.0) return 1.0;
    vec4 toLight = uSplatShadowLight[slot];
    mat4 m = uSplatShadowMatrix[slot];
    // Orient the splat's normal toward the light; how much the surface leans away from it.
    float ndl = dot(normal, toLight.xyz);
    vec3 n = ndl < 0.0 ? -normal : normal;
    ndl = abs(ndl);
    float sinTheta = sqrt(max(0.0, 1.0 - ndl * ndl));
    // Bias toward the light: a fixed part, plus the splat's own footprint where its neighbours
    // on the same surface can be in the map (scaled by the surface's slope for flat splats).
    float sizeBias = params.w * rl_splatSize * mix(1.0, sinTheta, rl_splatFlat);
    // Slope-scaled bias in shadow-map texels: a texel covers a range of depths on a surface that
    // leans away from the light.
    float tanTheta = mix(1.0, min(sinTheta / max(ndl, 0.05), 4.0), rl_splatFlat);
    float texelBias = uSplatShadowTexel.z * (slot == 0 ? uSplatShadowTexel.x : uSplatShadowTexel.y) * tanTheta;
    vec3 p = worldPos + toLight.xyz * (params.z + sizeBias + texelBias);
    vec4 sc = m * vec4(p, 1.0);
    vec2 uv = sc.xy / sc.w;
    if (uv.x <= 0.0 || uv.y <= 0.0 || uv.x >= 1.0 || uv.y >= 1.0) return 1.0;
    float z = clamp(sc.z / sc.w, 0.0, 1.0) - 0.0001;
    float radius = params.y + rl_splatSize * toLight.w;
    float lit = rl_shadowSample(slot, vec3(uv, z));
    int taps = int(uSplatShadowGlobal.x + 0.5);
    if (taps <= 0) return 1.0 - params.x * (1.0 - lit);
    // Receiver plane: the filter taps sample the surface around the splat, so follow its depth
    // slope in the map (flat splats only; a round blob has no plane).
    vec2 grad = vec2(0.0);
    if (rl_splatFlat > 0.3) {
        vec3 t1 = normalize(cross(n, abs(n.y) < 0.99 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0)));
        vec3 t2 = cross(n, t1);
        vec3 a = mat3(m) * t1;
        vec3 b = mat3(m) * t2;
        float det = a.x * b.y - a.y * b.x;
        if (abs(det) > 1e-8) {
            grad = vec2(a.z * b.y - b.z * a.y, a.x * b.z - b.x * a.z) / det;
            float uScale = length(vec3(m[0][0], m[1][0], m[2][0]));
            float zScale = length(vec3(m[0][2], m[1][2], m[2][2]));
            float maxGrad = 4.0 * zScale / max(uScale, 1e-6);
            float g = length(grad);
            if (g > maxGrad) grad *= maxGrad / g;
        }
    }
    float weight = 1.0;
    for (int i = 0; i < 8; i++) {
        if (i >= taps) break;
        // Most splats are fully lit or fully shadowed: stop after the first four taps agree.
        if (i == 4 && (lit == 0.0 || lit == weight)) break;
        vec2 o = RL_SHADOW_DISK[i] * radius;
        // Stay below the far plane: a receiver beyond it compares at ~1, so an empty texel must
        // still read lit after the slope offset.
        lit += rl_shadowSample(slot, vec3(uv + o, min(z + dot(o, grad), 0.9999)));
        weight += 1.0;
    }
    return 1.0 - params.x * (1.0 - lit / weight);
}
#else
float splatShadowFactor(int slot, vec3 worldPos, vec3 normal) { return 1.0; }
#endif

// ---- Spot and point lights: a copy of the engine's clustered shadow atlas (localShadows.ts) ----
// uSplatLocalPos[slot]     xyz light position, w 1 / range (0 = slot unused)
// uSplatLocalParams[slot]  x strength, y kind (1 spot, 2 point), z bias (world), w bias per unit splat size
// uSplatLocalRect[slot]    spot: the light's tile in the atlas (x, y, w, h); point: (x, y, face size, -)
// uSplatLocalSpot[slot]    spot: xyz direction it shines, w cos(outer cone angle)
// uSplatLocalTexel[slot]   x world size of a map texel per unit of distance, y filter radius (atlas uv),
//                          z largest filter radius (atlas uv), w atlas uv per world unit per unit of distance
// uSplatLocalMatrix[slot]  spot: world → atlas (uv, depth)
// uSplatLocalAtlas         x atlas size (px), y edge pixels around each point-light face, z slope bias texels,
//                          w slots in use
uniform vec4 uSplatLocalPos[4];
uniform vec4 uSplatLocalParams[4];
uniform vec4 uSplatLocalRect[4];
uniform vec4 uSplatLocalSpot[4];
uniform vec4 uSplatLocalTexel[4];
uniform mat4 uSplatLocalMatrix[4];
uniform vec4 uSplatLocalAtlas;
#if !defined(SHADOW_PASS) && !defined(PICK_PASS) && !defined(PREPASS_PASS)
uniform highp sampler2DShadow uSplatShadowAtlas;
// The engine's cube layout in the atlas (clusteredLightUtils: getCubemapAtlasCoordinates).
vec2 rl_cubeAtlasUv(vec4 rect, vec3 dir) {
    vec3 a = abs(dir);
    float ma;
    vec2 uv;
    vec2 tile;
    if (a.z >= a.x && a.z >= a.y) {
        ma = 0.5 / a.z;
        uv = vec2(dir.z < 0.0 ? -dir.x : dir.x, -dir.y);
        tile = vec2(2.0, dir.z < 0.0 ? 1.0 : 0.0);
    } else if (a.y >= a.x) {
        ma = 0.5 / a.y;
        uv = vec2(dir.x, dir.y < 0.0 ? -dir.z : dir.z);
        tile = vec2(1.0, dir.y < 0.0 ? 1.0 : 0.0);
    } else {
        ma = 0.5 / a.x;
        uv = vec2(dir.x < 0.0 ? dir.z : -dir.z, -dir.y);
        tile = vec2(0.0, dir.x < 0.0 ? 1.0 : 0.0);
    }
    uv = uv * ma + 0.5;
    float offset = uSplatLocalAtlas.y / (uSplatLocalAtlas.x * rect.z);
    uv = uv * (1.0 - 2.0 * offset) + offset;
    return (uv + tile) * rect.z + rect.xy;
}
// How much a spot / point light reaches the splat (1 = fully). With fadeWithLight (relighting
// off: the shadow darkens the baked colour) the shadow fades out toward the edge of the light's
// range and cone, where the light itself would add little.
float splatLocalShadowFactor(int slot, vec3 worldPos, vec3 normal, bool fadeWithLight) {
    vec4 lp = uSplatLocalPos[slot];
    if (lp.w <= 0.0) return 1.0;
    vec4 params = uSplatLocalParams[slot];
    vec3 toLight = lp.xyz - worldPos;
    float dist = length(toLight);
    float rel = dist * lp.w;
    if (rel >= 1.0 || dist < 1e-4) return 1.0;
    vec3 l = toLight / dist;
    float fade = 1.0;
    bool isSpot = params.y < 1.5;
    if (isSpot) {
        vec4 spot = uSplatLocalSpot[slot];
        float c = dot(-l, spot.xyz);
        if (c <= spot.w) return 1.0;
        if (fadeWithLight) fade *= smoothstep(spot.w, mix(spot.w, 1.0, 0.3), c);
    }
    if (fadeWithLight) fade *= clamp(1.0 - rel * rel, 0.0, 1.0);
    vec4 texel = uSplatLocalTexel[slot];
    float ndl = abs(dot(normal, l));
    float sinTheta = sqrt(max(0.0, 1.0 - ndl * ndl));
    float tanTheta = mix(1.0, min(sinTheta / max(ndl, 0.05), 4.0), rl_splatFlat);
    float texelWorld = texel.x * dist;
    float bias = params.z + params.w * rl_splatSize * mix(1.0, sinTheta, rl_splatFlat) + uSplatLocalAtlas.z * texelWorld * tanTheta;
    vec3 p = worldPos + l * min(bias, dist * 0.5);
    vec4 rect = uSplatLocalRect[slot];
    vec3 coord;
    if (isSpot) {
        vec4 sc = uSplatLocalMatrix[slot] * vec4(p, 1.0);
        coord = sc.xyz / sc.w;
        if (coord.x <= rect.x || coord.y <= rect.y || coord.x >= rect.x + rect.z || coord.y >= rect.y + rect.w) return 1.0;
        coord.z = clamp(coord.z, 0.0, 1.0) - 0.00002;
    } else {
        vec3 d = p - lp.xyz;
        coord = vec3(rl_cubeAtlasUv(rect, d), min(length(d) * lp.w, 0.9999));
    }
    float lit = texture(uSplatShadowAtlas, coord);
    int taps = int(uSplatShadowGlobal.x + 0.5);
    if (taps > 0) {
        // the light's softness plus the splat's footprint, kept inside the light's tile border
        float radius = min(texel.y + rl_splatSize * texel.w / dist, texel.z);
        float weight = 1.0;
        for (int i = 0; i < 8; i++) {
            if (i >= taps) break;
            if (i == 4 && (lit == 0.0 || lit == weight)) break;
            lit += texture(uSplatShadowAtlas, vec3(coord.xy + RL_SHADOW_DISK[i] * radius, coord.z));
            weight += 1.0;
        }
        lit /= weight;
    }
    return 1.0 - params.x * fade * (1.0 - lit);
}
#else
float splatLocalShadowFactor(int slot, vec3 worldPos, vec3 normal, bool fadeWithLight) { return 1.0; }
#endif
`;

export const SPLAT_SHADOW_WGSL = /* wgsl */ `
uniform uSplatShadowParams: array<vec4f, 2>;
uniform uSplatShadowLight: array<vec4f, 2>;
uniform uSplatShadowMatrix: array<mat4x4f, 2>;
uniform uSplatShadowGlobal: vec4f;
uniform uSplatShadowTexel: vec4f;
#if !defined(SHADOW_PASS) && !defined(PICK_PASS) && !defined(PREPASS_PASS)
var uSplatShadowMap0: texture_depth_2d;
var uSplatShadowMap0Sampler: sampler_comparison;
var uSplatShadowMap1: texture_depth_2d;
var uSplatShadowMap1Sampler: sampler_comparison;
var<private> rl_shadowDisk: array<vec2f, 8> = array<vec2f, 8>(
    vec2f(-0.613, 0.617), vec2f(0.170, -0.040), vec2f(-0.299, -0.792), vec2f(0.645, 0.493),
    vec2f(-0.651, -0.118), vec2f(0.421, -0.655), vec2f(0.003, 0.917), vec2f(0.930, -0.110)
);
fn rl_shadowSample(slot: i32, coord: vec3f) -> f32 {
    if (slot == 0) {
        return textureSampleCompareLevel(uSplatShadowMap0, uSplatShadowMap0Sampler, coord.xy, coord.z);
    }
    return textureSampleCompareLevel(uSplatShadowMap1, uSplatShadowMap1Sampler, coord.xy, coord.z);
}
fn splatShadowFactor(slot: i32, worldPos: vec3f, normal: vec3f) -> f32 {
    let params = uniform.uSplatShadowParams[slot];
    if (params.x <= 0.0) { return 1.0; }
    let toLight = uniform.uSplatShadowLight[slot];
    let m = uniform.uSplatShadowMatrix[slot];
    // Orient the splat's normal toward the light; how much the surface leans away from it.
    let ndlSigned = dot(normal, toLight.xyz);
    var n = normal;
    if (ndlSigned < 0.0) { n = -normal; }
    let ndl = abs(ndlSigned);
    let sinTheta = sqrt(max(0.0, 1.0 - ndl * ndl));
    let sizeBias = params.w * rl_splatSize * mix(1.0, sinTheta, rl_splatFlat);
    let tanTheta = mix(1.0, min(sinTheta / max(ndl, 0.05), 4.0), rl_splatFlat);
    var texelSize = uniform.uSplatShadowTexel.y;
    if (slot == 0) { texelSize = uniform.uSplatShadowTexel.x; }
    let texelBias = uniform.uSplatShadowTexel.z * texelSize * tanTheta;
    let p = worldPos + toLight.xyz * (params.z + sizeBias + texelBias);
    let sc = m * vec4f(p, 1.0);
    let uv = sc.xy / sc.w;
    if (uv.x <= 0.0 || uv.y <= 0.0 || uv.x >= 1.0 || uv.y >= 1.0) { return 1.0; }
    let z = clamp(sc.z / sc.w, 0.0, 1.0) - 0.0001;
    let radius = params.y + rl_splatSize * toLight.w;
    var lit = rl_shadowSample(slot, vec3f(uv, z));
    let taps = i32(uniform.uSplatShadowGlobal.x + 0.5);
    if (taps <= 0) { return 1.0 - params.x * (1.0 - lit); }
    var grad = vec2f(0.0);
    if (rl_splatFlat > 0.3) {
        var up = vec3f(0.0, 1.0, 0.0);
        if (abs(n.y) >= 0.99) { up = vec3f(1.0, 0.0, 0.0); }
        let t1 = normalize(cross(n, up));
        let t2 = cross(n, t1);
        let m3 = mat3x3f(m[0].xyz, m[1].xyz, m[2].xyz);
        let a = m3 * t1;
        let b = m3 * t2;
        let det = a.x * b.y - a.y * b.x;
        if (abs(det) > 1e-8) {
            grad = vec2f(a.z * b.y - b.z * a.y, a.x * b.z - b.x * a.z) / det;
            let uScale = length(vec3f(m[0][0], m[1][0], m[2][0]));
            let zScale = length(vec3f(m[0][2], m[1][2], m[2][2]));
            let maxGrad = 4.0 * zScale / max(uScale, 1e-6);
            let g = length(grad);
            if (g > maxGrad) { grad = grad * (maxGrad / g); }
        }
    }
    var weight = 1.0;
    for (var i: i32 = 0; i < 8; i++) {
        if (i >= taps) { break; }
        if (i == 4 && (lit == 0.0 || lit == weight)) { break; }
        let o = rl_shadowDisk[i] * radius;
        lit += rl_shadowSample(slot, vec3f(uv + o, min(z + dot(o, grad), 0.9999)));
        weight += 1.0;
    }
    return 1.0 - params.x * (1.0 - lit / weight);
}
#else
fn splatShadowFactor(slot: i32, worldPos: vec3f, normal: vec3f) -> f32 { return 1.0; }
#endif

// ---- Spot and point lights: the engine's clustered shadow atlas (see the GLSL version) ----
uniform uSplatLocalPos: array<vec4f, 4>;
uniform uSplatLocalParams: array<vec4f, 4>;
uniform uSplatLocalRect: array<vec4f, 4>;
uniform uSplatLocalSpot: array<vec4f, 4>;
uniform uSplatLocalTexel: array<vec4f, 4>;
uniform uSplatLocalMatrix: array<mat4x4f, 4>;
uniform uSplatLocalAtlas: vec4f;
#if !defined(SHADOW_PASS) && !defined(PICK_PASS) && !defined(PREPASS_PASS)
var uSplatShadowAtlas: texture_depth_2d;
var uSplatShadowAtlasSampler: sampler_comparison;
fn rl_cubeAtlasUv(rect: vec4f, dir: vec3f) -> vec2f {
    let a = abs(dir);
    var ma: f32;
    var uv: vec2f;
    var tile: vec2f;
    if (a.z >= a.x && a.z >= a.y) {
        ma = 0.5 / a.z;
        uv = vec2f(select(dir.x, -dir.x, dir.z < 0.0), -dir.y);
        tile = vec2f(2.0, select(0.0, 1.0, dir.z < 0.0));
    } else if (a.y >= a.x) {
        ma = 0.5 / a.y;
        uv = vec2f(dir.x, select(dir.z, -dir.z, dir.y < 0.0));
        tile = vec2f(1.0, select(0.0, 1.0, dir.y < 0.0));
    } else {
        ma = 0.5 / a.x;
        uv = vec2f(select(-dir.z, dir.z, dir.x < 0.0), -dir.y);
        tile = vec2f(0.0, select(0.0, 1.0, dir.x < 0.0));
    }
    uv = uv * ma + 0.5;
    let offset = uniform.uSplatLocalAtlas.y / (uniform.uSplatLocalAtlas.x * rect.z);
    uv = uv * (1.0 - 2.0 * offset) + offset;
    return (uv + tile) * rect.z + rect.xy;
}
fn splatLocalShadowFactor(slot: i32, worldPos: vec3f, normal: vec3f, fadeWithLight: bool) -> f32 {
    let lp = uniform.uSplatLocalPos[slot];
    if (lp.w <= 0.0) { return 1.0; }
    let params = uniform.uSplatLocalParams[slot];
    let toLight = lp.xyz - worldPos;
    let dist = length(toLight);
    let rel = dist * lp.w;
    if (rel >= 1.0 || dist < 1e-4) { return 1.0; }
    let l = toLight / dist;
    var fade = 1.0;
    let isSpot = params.y < 1.5;
    if (isSpot) {
        let spot = uniform.uSplatLocalSpot[slot];
        let c = dot(-l, spot.xyz);
        if (c <= spot.w) { return 1.0; }
        if (fadeWithLight) { fade = fade * smoothstep(spot.w, mix(spot.w, 1.0, 0.3), c); }
    }
    if (fadeWithLight) { fade = fade * clamp(1.0 - rel * rel, 0.0, 1.0); }
    let texel = uniform.uSplatLocalTexel[slot];
    let ndl = abs(dot(normal, l));
    let sinTheta = sqrt(max(0.0, 1.0 - ndl * ndl));
    let tanTheta = mix(1.0, min(sinTheta / max(ndl, 0.05), 4.0), rl_splatFlat);
    let texelWorld = texel.x * dist;
    let bias = params.z + params.w * rl_splatSize * mix(1.0, sinTheta, rl_splatFlat) + uniform.uSplatLocalAtlas.z * texelWorld * tanTheta;
    let p = worldPos + l * min(bias, dist * 0.5);
    let rect = uniform.uSplatLocalRect[slot];
    var coord: vec3f;
    if (isSpot) {
        let sc = uniform.uSplatLocalMatrix[slot] * vec4f(p, 1.0);
        coord = sc.xyz / sc.w;
        if (coord.x <= rect.x || coord.y <= rect.y || coord.x >= rect.x + rect.z || coord.y >= rect.y + rect.w) { return 1.0; }
        coord.z = clamp(coord.z, 0.0, 1.0) - 0.00002;
    } else {
        let d = p - lp.xyz;
        coord = vec3f(rl_cubeAtlasUv(rect, d), min(length(d) * lp.w, 0.9999));
    }
    var lit = textureSampleCompareLevel(uSplatShadowAtlas, uSplatShadowAtlasSampler, coord.xy, coord.z);
    let taps = i32(uniform.uSplatShadowGlobal.x + 0.5);
    if (taps > 0) {
        let radius = min(texel.y + rl_splatSize * texel.w / dist, texel.z);
        var weight = 1.0;
        for (var i: i32 = 0; i < 8; i++) {
            if (i >= taps) { break; }
            if (i == 4 && (lit == 0.0 || lit == weight)) { break; }
            lit += textureSampleCompareLevel(uSplatShadowAtlas, uSplatShadowAtlasSampler, coord.xy + rl_shadowDisk[i] * radius, coord.z);
            weight += 1.0;
        }
        lit = lit / weight;
    }
    return 1.0 - params.x * fade * (1.0 - lit);
}
#else
fn splatLocalShadowFactor(slot: i32, worldPos: vec3f, normal: vec3f, fadeWithLight: bool) -> f32 { return 1.0; }
#endif
`;

/** Uniform / texture names, shared with ShadowSystem. */
export const SPLAT_SHADOW_UNIFORMS = {
    params: 'uSplatShadowParams[0]',
    light: 'uSplatShadowLight[0]',
    matrix: 'uSplatShadowMatrix[0]',
    global: 'uSplatShadowGlobal',
    texel: 'uSplatShadowTexel',
    /** Declared in the mirror-clip snippet; x largest casting splat, y least casting opacity. */
    casterLimits: 'uSplatCasterLimits',
    maps: ['uSplatShadowMap0', 'uSplatShadowMap1'],
    /** Spot / point lights: a copy of the engine's shadow atlas (other uniforms: localShadows.ts). */
    atlas: 'uSplatShadowAtlas',
} as const;

